import { z } from 'zod'
import { convertJsonSchemaToZod, JSONSchema } from 'zod-from-json-schema'
import {
  chatChannelFailedSchema,
  chatConnectionFailedSchema,
  filtersAppliedSchema,
  keyboardShortcutTriggeredSchema,
  leaveFeedSchema,
  memberChatOpenedSchema,
  memberNotesOpenedSchema,
  memberOpenedSchema,
  memberResolvedSchema,
  memberSidebarOpenedSchema,
  memberSnoozedSchema,
  messageSentSchema,
  openFeedSchema,
  openUpdateSchema,
  openUpdatesListSchema,
  pushToSmsSchema,
  recommendationsOpenedSchema,
  selectMembersListTabSchema,
  sessionStartedSchema,
  sessionTimeoutModalSchema,
  swordDeskTicketCreatedSchema,
  taskCompletedSchema,
  uiTransitionSchema,
  uiBlockerSchema,
} from './schemas'
import { isZodSchema } from '@/utils/zod/zod-utils'
import { callStartedSchema } from './schemas/call-started'

/*
 * Central Segment event registry.
 * Maps event keys to { key, name, schema } objects.
 * Schemas can be Zod or JSON Schema (converted to Zod at runtime).
 */
export const EVENT_REGISTRY = {
  ADD_NOTE: { key: 'ADD_NOTE', name: 'Add Note', schema: z.object({}) },
  AUTO_LOGOUT_POPUP_OPEN: { key: 'AUTO_LOGOUT_POPUP_OPEN', name: 'Auto-logout pop-up Open', schema: z.object({}) },
  CALL_STARTED: { key: 'CALL_STARTED', name: 'Call Started', schema: callStartedSchema },
  CHAT_SCROLL: { key: 'CHAT_SCROLL', name: 'Chat scroll', schema: z.object({}) },
  CHAT_CHANNEL_FAILED: { key: 'CHAT_CHANNEL_FAILED', name: 'Chat Channel Failed', schema: chatChannelFailedSchema },
  CHAT_CONNECTION_FAILED: { key: 'CHAT_CONNECTION_FAILED', name: 'Chat Connection Failed', schema: chatConnectionFailedSchema },
  INVALID_CHANNELS_ERROR: { key: 'INVALID_CHANNELS_ERROR', name: 'Instance Channels Error', schema: z.object({}) },
  ENTER_RECORDS_PAGE: { key: 'ENTER_RECORDS_PAGE', name: 'Enter Records Page', schema: z.object({}) },
  FILTER_APPLIED: { key: 'FILTER_APPLIED', name: 'Filter Applied', schema: z.object({}) },
  FILTERS_APPLIED: { key: 'FILTERS_APPLIED', name: 'Applied Filters', schema: filtersAppliedSchema },
  HOVER_ALERTS: { key: 'HOVER_ALERTS', name: 'Hover Alerts', schema: z.object({}) },
  HOVER_KIT_STATUS: { key: 'HOVER_KIT_STATUS', name: 'Hover kit status', schema: z.object({}) },
  HOVER_SESSIONS: { key: 'HOVER_SESSIONS', name: 'Hover Sessions', schema: z.object({}) },
  KEYBOARD_SHORTCUT_TRIGGERED: { key: 'KEYBOARD_SHORTCUT_TRIGGERED', name: 'Keyboard Shortcut Triggered', schema: keyboardShortcutTriggeredSchema },
  LEAVE_FEED: { key: 'LEAVE_FEED', name: 'Leaves the Feed', schema: leaveFeedSchema },
  MEMBER_CHAT_OPENED: { key: 'MEMBER_CHAT_OPENED', name: 'Member Chat Opened', schema: memberChatOpenedSchema },
  MEMBER_NOTES_OPENED: { key: 'MEMBER_NOTES_OPENED', name: 'Member Notes Opened', schema: memberNotesOpenedSchema },
  MEMBER_OPENED: { key: 'MEMBER_OPENED', name: 'Member Opened', schema: memberOpenedSchema },
  MEMBER_RESOLVED: { key: 'MEMBER_RESOLVED', name: 'Member Resolved', schema: memberResolvedSchema },
  MEMBER_SIDEBAR_OPENED: { key: 'MEMBER_SIDEBAR_OPENED', name: 'Opened Sidepanel', schema: memberSidebarOpenedSchema },
  MEMBER_SNOOZED: { key: 'MEMBER_SNOOZED', name: 'Member Snoozed', schema: memberSnoozedSchema },
  MEMBERS_LIST_FILTERED: { key: 'MEMBERS_LIST_FILTERED', name: 'Member\'s List Filtered', schema: z.object({}) },
  MEMBERS_LIST_SORTED: { key: 'MEMBERS_LIST_SORTED', name: 'Member\'s List Sorted', schema: z.object({}) },
  MESSAGE_SENT: { key: 'MESSAGE_SENT', name: 'Message Sent', schema: messageSentSchema },
  OPEN_CHAT_MODE: { key: 'OPEN_CHAT_MODE', name: 'Open Chat Mode', schema: openFeedSchema },
  OPEN_FEED: { key: 'OPEN_FEED', name: 'Opens Feed', schema: openFeedSchema },
  OPEN_MEMBERS_LIST: { key: 'OPEN_MEMBERS_LIST', name: 'Opens Members List', schema: z.object({}) },
  OPEN_UPDATE: { key: 'OPEN_UPDATE', name: 'Open Update', schema: openUpdateSchema },
  OPEN_UPDATES_LIST: { key: 'OPEN_UPDATES_LIST', name: 'Open Updates List', schema: openUpdatesListSchema },
  PUSH_TO_SMS: { key: 'PUSH_TO_SMS', name: 'Push to SMS', schema: pushToSmsSchema },
  RECOMMENDATIONS_OPENED: { key: 'RECOMMENDATIONS_OPENED', name: 'Recommendations Opened', schema: recommendationsOpenedSchema },
  SD_TICKET_CREATED: { key: 'SD_TICKET_CREATED', name: 'SD ticket Created', schema: swordDeskTicketCreatedSchema },
  SD_TICKETS_VIEWED: { key: 'SD_TICKETS_VIEWED', name: 'SD tickets Viewed', schema: z.object({}) },
  SELECT_MEMBERS_LIST_TAB: { key: 'SELECT_MEMBERS_LIST_TAB', name: 'Select Members List Tab', schema: selectMembersListTabSchema },
  SESSION_STARTED: { key: 'SESSION_STARTED', name: 'Session Started', schema: sessionStartedSchema },
  SESSION_TIMEOUT_MODAL_EXPIRED: { key: 'SESSION_TIMEOUT_MODAL_EXPIRED', name: 'Session Timeout Expired', schema: z.object({}) },
  SESSION_TIMEOUT_MODAL_SHOWN: { key: 'SESSION_TIMEOUT_MODAL_SHOWN', name: 'Session Timeout Shown', schema: sessionTimeoutModalSchema },
  SESSION_TIMEOUT_MODAL_SNOOZED: { key: 'SESSION_TIMEOUT_MODAL_SNOOZED', name: 'Session Timeout Snoozed', schema: sessionTimeoutModalSchema },
  TASK_COMPLETED: { key: 'TASK_COMPLETED', name: 'Task Completed', schema: taskCompletedSchema },
  UI_TRANSITION_STARTED: { key: 'UI_TRANSITION_STARTED', name: 'UI Transition Started', schema: uiTransitionSchema },
  UI_TRANSITION_ENDED: { key: 'UI_TRANSITION_ENDED', name: 'UI Transition Ended', schema: uiTransitionSchema },
  UI_BLOCKER: { key: 'UI_BLOCKER', name: 'UI Blocker', schema: uiBlockerSchema },
} as const

/* Segment event keys synced with EVENT_REGISTRY for autocomplete and reference safety. (e.g. EVENT_KEYS.SESSION_STARTED). */
export const EVENT_KEYS = Object.freeze(
  Object.fromEntries((Object.keys(EVENT_REGISTRY) as EventKeysType[]).map((k) => [k, k])) as Record<EventKeysType, EventKeysType>,
)

/* Map event schemas from EVENT_REGISTRY, converting JSON Schemas to Zod at runtime for validation and type inference. */
export const EVENT_SCHEMAS: Partial<Record<EventKeysType, z.ZodTypeAny>> =
  (Object.keys(EVENT_REGISTRY) as EventKeysType[]).reduce((acc, key) => {
    const currentEvent = EVENT_REGISTRY[key]
    const schema = currentEvent.schema

    let zodSchema: z.ZodTypeAny | undefined

    if (schema) {
      zodSchema = isZodSchema(schema)
        ? schema
        : convertJsonSchemaToZod(schema as JSONSchema)
    }

    if (zodSchema) acc[key] = zodSchema

    return acc
  }, {} as Partial<Record<EventKeysType, z.ZodTypeAny>>)

/* Segment Types */
export type EventKeysType = keyof typeof EVENT_REGISTRY

export type FromSchema<K extends EventKeysType> =
  (typeof EVENT_REGISTRY)[K] extends { schema: infer S }
  ? (S extends z.ZodTypeAny ? z.infer<S> : Record<string, unknown>)
  : Record<string, unknown>

/* Map of event payloads type used by the event tracker (compile‑time). */
export type EventPayloads = { [T in EventKeysType]: FromSchema<T> }
